/**
 * Uppy 업로드 위젯 (React)
 * - 레거시 백엔드(타임리프)와 동일한 Tus API 사용: /api/tus/file/upload, storagetemp, download
 * - index.html 로드된 uppy.min.js, uppy.locale.js, uppy.min.css 활용
 * - UI만 위젯 컴포넌트화, 뒷단은 기존 백엔드 서버 활용
 *
 * 사용 예:
 *   const uppyRef = useRef();
 *   <UppyUploadWidget
 *     ref={uppyRef}
 *     height={320}
 *     maxFileCount={10}
 *     allowedExtensions={['pdf', 'doc', 'docx', 'hwp', 'hwpx', 'jpg', 'png']}
 *     onComplete={(result) => console.log('업로드 완료', result)}
 *     disableDragDrop  // 기본 true — 파일 선택만 허용. 드래그앤드롭 필요 시 false
 *   />;
 *   // 제출 시: uppyRef.current.getTempFileUrls(), uppyRef.current.getRemoveFileUrls()
 */
import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Http from '../../core/Http';
import { buildLoginUrl } from '../../utils/authRedirect';
import './UppyUploadWidget.css';

const DEFAULT_ALLOWED_EXTENSIONS = ['zip', 'text', 'pptx', 'xls', 'ppt', 'xlsx', 'jpg', 'hwp', 'hwpx', 'pdf', 'doc', 'jpeg', 'png', 'gif', 'txt', 'csv', 'mp4', 'json', 'docx'];
const DEFAULT_MAX_COUNT = 100;
const DEFAULT_MAX_SIZE_MB = 100;
const CHUNK_SIZE = 50000000;

function getUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 3) | 8;
    return v.toString(16);
  });
}

/** disableDragDrop 시: 드롭 안내 문구 제거, 파일 선택(탐색기)만 허용 */
const KO_STRINGS_NO_DRAG = {
  dropPasteFiles: '',
  dropHint: '',
  dropHereOr: '%{browse}',
};

const UppyUploadWidget = forwardRef(function UppyUploadWidget(
  {
    /** 업로드 엔드포인트: false면 기본 /api/tus, true면 /api/_fs_tus (JWT) */
    useVariableEndpoint = false,
    /** 인라인 대시보드 너비 */
    width = '100%',
    /** 인라인 대시보드 높이(px) */
    height = 304,
    /** 최대 파일 개수 */
    maxFileCount = DEFAULT_MAX_COUNT,
    /** 허용 확장자 (소문자) */
    allowedExtensions = DEFAULT_ALLOWED_EXTENSIONS,
    /** 최대 파일 크기(MB) */
    maxSizeMB = DEFAULT_MAX_SIZE_MB,
    /** 자동 업로드 진행 여부 */
    autoProceed = true,
    /** 수정 모드 시 전달하는 uid (메타에 포함) */
    fileUid = null,
    /** 업로드 완료 시 파일 메타 콜백 */
    onUploadSuccess = null,
    /** 전체 완료 콜백 */
    onComplete = null,
    /** 에러 콜백 */
    onError = null,
    /** 파일 제거 콜백 (삭제 대상 목록 반영 시) */
    onFileRemoved = null,
    /** 컴포넌트 id (여러 인스턴스 구분) */
    id: widgetId = 'uppy-upload-widget',
    className = '',
    /** true면 드래그앤드롭 비활성화(파일 선택·내 기기 버튼만 사용). 기본 true */
    disableDragDrop = true,
    /** Uppy 인스턴스 초기화 완료 시 (수정 모드 기존 파일 주입 타이밍용) */
    onReady = null,
  },
  ref
) {
  const { t } = useTranslation();
  const koStrings = useMemo(() => ({
    dropPasteFiles: '',
    browseFiles: t('components.upload.browseFiles'),
    myDevice: t('components.upload.myDevice'),
    dropHereOr: t('components.upload.dropHereOr'),
    browse: t('components.upload.browse'),
    dropHint: t('components.upload.dropHint'),
  }), [t]);

  const containerRef = useRef(null);
  const uppyRef = useRef(null);
  const uploadUUIDRef = useRef(null);
  const removesRef = useRef([]);
  const currentFilesRef = useRef({});
  const propsRef = useRef({ maxFileCount, maxSizeMB, allowedExtensions, autoProceed, fileUid });
  const callbacksRef = useRef({ onUploadSuccess, onComplete, onError, onFileRemoved });
  const onReadyRef = useRef(onReady);

  useEffect(() => {
    propsRef.current = { maxFileCount, maxSizeMB, allowedExtensions, autoProceed, fileUid };
  }, [maxFileCount, maxSizeMB, allowedExtensions, autoProceed, fileUid]);

  useEffect(() => {
    callbacksRef.current = { onUploadSuccess, onComplete, onError, onFileRemoved };
  }, [onUploadSuccess, onComplete, onError, onFileRemoved]);

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  const uploadEndpoint = useVariableEndpoint ? '/api/_fs_tus/file/upload' : '/api/tus/file/upload';
  const removeEndpoint = useVariableEndpoint ? '/api/_fs_tus/file/storagetemp' : '/api/tus/file/storagetemp';
  const downloadEndpoint = useVariableEndpoint ? '/api/_fs_tus/file/download' : '/api/tus/file/download';

  const [jwtToken, setJwtToken] = useState(null);
  const [jwtLoaded, setJwtLoaded] = useState(!useVariableEndpoint);

  useEffect(() => {
    if (!useVariableEndpoint) {
      setJwtLoaded(true);
      return;
    }
    Http.get('/cetus/files/bigfile/jwttoken.json', { baseURL: '' })
      .then((data) => {
        setJwtToken(data?.jwtToken ?? null);
        setJwtLoaded(true);
      })
      .catch(() => {
        setJwtToken(null);
        setJwtLoaded(true);
      });
  }, [useVariableEndpoint]);

  useEffect(() => {
    const Uppy = typeof window !== 'undefined' ? window.Uppy : null;
    const winLocale = typeof window !== 'undefined' ? window.locale : null;
    if (!Uppy || !containerRef.current) return;
    if (useVariableEndpoint && !jwtLoaded) return;

    const { Dashboard, Tus } = Uppy;
    const uuid = getUUID();
    uploadUUIDRef.current = uuid;

    const baseStrings = disableDragDrop
      ? { ...koStrings, ...KO_STRINGS_NO_DRAG }
      : { ...koStrings };
    const locale = winLocale && winLocale.strings
      ? winLocale
      : { pluralize: () => 0, strings: baseStrings };

    const uppy = new Uppy.Uppy({
      id: `uppy-${widgetId}-${uuid.slice(0, 8)}`,
      locale,
      autoProceed: propsRef.current.autoProceed,
      onBeforeFileAdded: (file) => {
        const { allowedExtensions: allowed, maxSizeMB: maxMB, maxFileCount: maxCount, fileUid: uid } = propsRef.current;
        const uploadMaxValue = 1024 * 1024;
        const maxBytes = maxMB * uploadMaxValue;

        // [수정 26.02.27] 이미 서버에 저장된 파일은 모든 검사(확장자, 개수, 용량)를 건너뜀
        if (file.meta?.saved === true || file.meta?.saved === 'Y') return file;

        const ext = (file.extension || '').toLowerCase();
        if (allowed.length && !allowed.includes(ext)) {
          if (typeof window !== 'undefined' && window.Util?.alert) {
            window.Util.alert(t('components.upload.invalidExtension'));
          } else {
            // eslint-disable-next-line no-alert
            alert(t('components.upload.invalidExtension'));
          }
          return false;
        }
        const count = uppy.getFiles().length;
        if (count >= maxCount) {
          if (typeof window !== 'undefined' && window.Util?.alert) {
            window.Util.alert(t('components.upload.maxCount', { count: maxCount }));
          } else {
            // eslint-disable-next-line no-alert
            alert(t('components.upload.maxCount', { count: maxCount }));
          }
          return false;
        }
        if (file.data?.size > maxBytes) {
          if (typeof window !== 'undefined' && window.Util?.alert) {
            window.Util.alert(t('components.upload.maxSize', { size: maxMB }));
          } else {
            // eslint-disable-next-line no-alert
            alert(t('components.upload.maxSize', { size: maxMB }));
          }
          return false;
        }
        const meta = { ...file.meta };
        if (uid != null) meta.uid = uid;
        return { ...file, meta };
      },
    });

    uppy.use(Dashboard, {
      inline: true,
      target: containerRef.current,
      width,
      height,
      showRemoveButtonAfterComplete: true,
      hideUploadButton: !autoProceed,
      locale: { strings: { ...baseStrings, ...(locale.strings || {}), ...(disableDragDrop ? KO_STRINGS_NO_DRAG : {}) } },
    });

    const tusHeaders = {
      'X-TUS-UUID': uuid,
      ...(useVariableEndpoint && jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {}),
    };

    uppy.use(Tus, {
      endpoint: uploadEndpoint,
      chunkSize: CHUNK_SIZE,
      headers: tusHeaders,
      onBeforeRequest: (req, file) => {
        currentFilesRef.current[req.getURL()] = file;
      },
      onAfterResponse: (req, res) => {
        const status = res.getStatus();
        if (status >= 200 && status < 300) {
          const value64 = res.getHeader('X-Response-Data');
          if (value64) {
            try {
              const value = atob(value64);
              const jsonObject = JSON.parse(value);
              const url = req.getURL();
              const file = currentFilesRef.current[url];
              if (file) {
                // 저장소의 물리적 경로/UUID
                file.meta.fileId = jsonObject.path || jsonObject.fileId;
                
                // DB의 시퀀스 형태 정수 PK (cetus_file.file_uid)
                file.meta.fileUid = jsonObject.file_uid || jsonObject.fileUid || jsonObject.uid;

                // 기존 백엔드 연동을 위해 json 정보 추가 저장
                if (jsonObject.orgFileNm) file.meta.name = jsonObject.orgFileNm;
              }
            } catch (e) {
              console.warn('[UppyUploadWidget] X-Response-Data parse error', e);
            }
          }
        }
      },
      onShouldRetry: (err, retryAttempt, options, next) => {
        const res = err?.originalResponse;
        const status = res?.getStatus?.();
        if (status === 405) {
          try {
            if (typeof window !== 'undefined' && window.signModal?.show) window.signModal.show();
          } catch {
            window.location.href = buildLoginUrl();
          }
          return false;
        }
        return next(err);
      },
    });

    uppy.on('upload-success', (file, upload) => {
      // [수정 26.02.27] 기존에 이미 저장된('Y' 또는 true) 파일이면 이벤트를 무시하여 메타데이터 오염 방지
      if (file.meta?.saved === 'Y' || file.meta?.saved === true) {
        return;
      }

      file.meta.saved = 'N';
      file.meta.name = file.name;
      const { onUploadSuccess: onUp } = callbacksRef.current;
      if (onUp) onUp(file, upload);
    });

    uppy.on('complete', (result) => {
      currentFilesRef.current = {};
      const { onComplete: onDone } = callbacksRef.current;
      if (onDone) onDone(result);
    });

    uppy.on('file-removed', (file, reason) => {
      // [수정 26.02.27] 이미 서버에 저장된('Y' 또는 true) 파일은 삭제 이벤트에서 제외
      // - saved : 'Y' -> removesRef에 추가 (서비스단을 태워서 삭제)
      // - saved : 'N' -> API를 통한 삭제
      if (reason === 'removed-by-user') {
        if ((file.meta?.saved === false || file.meta?.saved === 'N') && file.meta?.fileId) {
          Http.delete(`${removeEndpoint}/${file.meta.fileId}`).catch((e) => {
            console.warn('[UppyUploadWidget] delete temp file failed', e);
          });
        } else if (file.meta?.saved !== undefined && file.meta?.saved !== 'N') {
          removesRef.current.push({
            orgFileNm: file.meta?.name,
            fileType: file.meta?.type,
            fileId: file.meta?.fileId,
            saved: file.meta?.saved === 'N' ? 'N' : 'Y',
            extension: file.extension,
          });
        }
      }
      const { onFileRemoved: onRemoved } = callbacksRef.current;
      if (onRemoved) onRemoved(file, reason);
    });

    uppy.on('error', (error) => {
      console.error('[UppyUploadWidget]', error);
      const { onError: onErr } = callbacksRef.current;
      if (onErr) onErr(error);
    });

    uppyRef.current = uppy;
    queueMicrotask(() => {
      onReadyRef.current?.(uppy);
    });

    return () => {
      if (uppyRef.current) {
        uppyRef.current.cancelAll();
        uppyRef.current.close();
        uppyRef.current = null;
      }
      removesRef.current = [];
      currentFilesRef.current = {};
    };
  }, [
    useVariableEndpoint,
    uploadEndpoint,
    removeEndpoint,
    downloadEndpoint,
    widgetId,
    jwtToken,
    jwtLoaded,
    width,
    height,
    autoProceed,
    disableDragDrop,
    koStrings,
    t,
  ]);

  /** 드래그앤드롭 이벤트 차단(캡처 단계) — Dashboard/DragDrop 핸들러보다 먼저 막음 */
  useEffect(() => {
    if (!disableDragDrop) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;
    const blockDragDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const types = ['dragenter', 'dragover', 'dragleave', 'drop'];
    types.forEach((type) => el.addEventListener(type, blockDragDrop, true));
    return () => {
      types.forEach((type) => el.removeEventListener(type, blockDragDrop, true));
    };
  }, [disableDragDrop, jwtLoaded, widgetId, koStrings, t]);

  // 동적 옵션 업데이트 (인스턴스 재생성 방지)
  useEffect(() => {
    if (uppyRef.current) {
      uppyRef.current.setOptions({
        autoProceed,
        restrictions: {
          maxNumberOfFiles: maxFileCount,
          maxFileSize: maxSizeMB * 1024 * 1024,
          allowedFileTypes: allowedExtensions.map(ext => `.${ext}`),
        },
      });
    }
  }, [autoProceed, maxFileCount, maxSizeMB, allowedExtensions]);

  useImperativeHandle(
    ref,
    () => ({
      getUppy: () => uppyRef.current,
      /** 등록하기 등 제출 시 호출. 미업로드 파일이 있으면 Tus 업로드 후 resolve. Promise<result> */
      uploadAll: () => {
        const uppy = uppyRef.current;
        if (!uppy) return Promise.resolve({ successful: [], failed: [] });
        const files = uppy.getFiles();
        // [수정 26.02.27] 업로드 대상에서 이미 저장된('Y') 파일은 확실히 제외
        const toUpload = files.filter((f) => f.meta?.saved !== 'Y' && f.meta?.saved !== true && !f.progress?.uploadComplete);
        if (toUpload.length === 0) return Promise.resolve({ successful: files, failed: [] });
        return uppy.upload(toUpload.map(f => f.id));
      },
      getTempFileUrls: () => {
        const uppy = uppyRef.current;
        if (!uppy) return [];
        const allFiles = uppy.getFiles();
        
        // [수정 26.02.27] 이미 서버에 저장된 파일(saved: 'Y' 또는 true)은 제외하고 신규 추가된 파일만 반환
        const filtered = allFiles.filter((x) => {
          const isSaved = x.meta?.saved === 'Y' || x.meta?.saved === true;
          return !isSaved;
        });

        // [수정 26.03.17] cetus_file.file_uid를 함께 반환하여
        // 오브젝트의 FILE 타입 속성에 file_uid를 그대로 저장할 수 있도록 확장
        return filtered.map((x) => ({
          orgFileNm: x.meta?.name || x.name, // name이 없으면 파일원본이름 사용
          fileType: x.meta?.type || x.type,
          fileId: x.meta?.fileId || x.id,
          fileUid: x.meta?.fileUid ?? null,
          saved: 'N',
          extension: x.extension,
        }));
      },
      getRemoveFileUrls: () => [...removesRef.current],
      reset: () => {
        const uppy = uppyRef.current;
        if (uppy) {
          uppy.getFiles().forEach((x) => uppy.removeFile(x.id));
        }
        removesRef.current = [];
      },
      addStaticFile: (file, isTemp) => {
        const uppy = uppyRef.current;
        if (!uppy) return;

        const src = file?.raw && typeof file.raw === 'object' ? file.raw : file;
        if (!src?.fileId) return;

        const displayName =
          src.orgFileNm || src.fileNm || src.FileNm || file?.name || String(src.fileId);
        const fileType = src.fileType || file?.fileType || 'application/octet-stream';
        const extFromField = String(src.extension || file?.extension || '')
          .toLowerCase()
          .replace(/^\./, '');
        const extFromName = (displayName.split('.').pop() || '').toLowerCase();
        const extension = extFromField || extFromName || 'bin';

        const b64nm = btoa(encodeURIComponent(displayName));
        const options = {};
        if (String(fileType).includes('image') && src.filePath) {
          options.preview = `${downloadEndpoint}/${src.filePath}/${b64nm}`;
        }

        const prevRestrictions = uppy.opts?.restrictions;
        const isSaved = !isTemp;
        const { allowedExtensions: allowedExts } = propsRef.current;
        let safeExtension = extension;
        if (Array.isArray(allowedExts) && allowedExts.length > 0) {
          if (!allowedExts.includes(safeExtension)) {
            if (String(fileType).startsWith('image/')) {
              safeExtension = allowedExts.includes('jpeg') ? 'jpeg' : allowedExts[0];
            }
          }
        }

        try {
          if (uppy.getFile(src.fileId)) return;

          // 서버에 이미 저장된 파일: Restrictions 전체 해제 후 추가 (null만으로는 일부 Uppy 버전에서 무시됨)
          if (isSaved) {
            uppy.setOptions({ restrictions: {} });
          }

          uppy.addFile({
            id: src.fileId,
            name: displayName,
            type: fileType,
            extension: safeExtension,
            data: new Blob([], { type: fileType }),
            meta: {
              url: src.filePath ? `${downloadEndpoint}/${src.filePath}/${b64nm}` : '',
              saved: isSaved ? 'Y' : 'N',
              fileId: src.fileId,
              fileUid: src.fileUid ?? file?.fileUid ?? null,
              name: displayName,
            },
            ...options,
          });

          if (uppy.getFile(src.fileId)) {
            uppy.setFileState(src.fileId, {
              progress: {
                uploadComplete: true,
                uploadStarted: true,
                bytesTotal: src.fileSize || 0,
                bytesUploaded: src.fileSize || 0,
              },
              meta: {
                ...uppy.getFile(src.fileId).meta,
                saved: isSaved ? 'Y' : 'N',
              },
            });
          }
        } catch (e) {
          console.warn('[UppyUploadWidget] addStaticFile failed:', e);
        } finally {
          if (isSaved) {
            uppy.setOptions({
              restrictions: prevRestrictions || {
                maxNumberOfFiles: propsRef.current.maxFileCount,
                maxFileSize: propsRef.current.maxSizeMB * 1024 * 1024,
                allowedFileTypes: propsRef.current.allowedExtensions.map((ext) => `.${ext}`),
              },
            });
          }
        }
      },
      isCompleted: () => {
        const uppy = uppyRef.current;
        if (!uppy) return true;
        return uppy.getFiles().reduce((acc, cur) => acc && cur.progress?.uploadComplete, true);
      },
      hasTempFiles: () => {
        const uppy = uppyRef.current;
        if (!uppy) return false;
        return uppy.getFiles().some((cur) => cur.meta?.saved !== true && cur.meta?.saved !== 'Y'); // [수정 26.02.27] true -> 'Y'
      },
      isModified: () => {
        const uppy = uppyRef.current;
        return (uppy?.getFiles().length || 0) > 0 || removesRef.current.length > 0;
      },
      getFiles: () => uppyRef.current?.getFiles() ?? [],
    }),
    [downloadEndpoint]
  );

  return (
    <div
      id={widgetId}
      className={`uppy-upload-widget ${disableDragDrop ? 'uppy-upload-widget--no-drag-drop' : ''} ${className}`.trim()}
      ref={containerRef}
    />
  );
});

export default UppyUploadWidget;

export { DEFAULT_ALLOWED_EXTENSIONS, DEFAULT_MAX_COUNT, DEFAULT_MAX_SIZE_MB };
